import {bookOfRules, rulesForCountries} from "~/helpers/postal-code-rules";
import { ref, computed } from "@nuxtjs/composition-api";
let getCMSLabels;
if (process.client) {
  import('~/helpers/globalLabels').then((module) => {
    getCMSLabels = module.getCMSLabels;
  });
}

// Method to fetch the max allowed length for a given field
const getMaxAllowedLength = (fieldKey) => {
  const maxAllowedLengths = {
    firstname: getCMSLabels("first-name-max-allowed-length"),
    lastname: getCMSLabels("last-name-max-allowed-characters"),
    streetAdditional: getCMSLabels("additional-address-max-allowed-characters"),
    streetaddress: getCMSLabels("street-address-max-allowed-characters"),
    city: getCMSLabels("city-max-allowed-characters"),
    phonenumber: getCMSLabels("phone-number-allowed-characters"),
    email: getCMSLabels("email-max-allowed-characters"),
  };

  return maxAllowedLengths[fieldKey.toLowerCase()] || 255;
};

// Method to fetch the validation message for max length
const getValidationMessage = (msgType) => {
  const messageMap = {
    maxLength: "max-allowed-length-validation-message",
    noLeadingNumber: "cannot-start-with-number-validation-message",
    invalidCharacter: "contains-invalid-characters-validation-message",
    invalidEmail: "invalid-email-validation-message",
    requiredField: "required-field-validation-message"
  };

  return getCMSLabels(messageMap[msgType] ?? '');
};

export function markFieldAsTouched(event, formValues, name=null) {
  const fieldName = name ?? event.target.getAttribute("name");
  if (fieldName && formValues[fieldName]) {
    formValues[fieldName].touched = true;
  }
  return formValues;
}

export function isFormValidated(formValues : Object) {
  return Object.values(formValues).every(field => field.touched && field.validation.valid);
}

export function touchedState(value: string | boolean | number): boolean {
  return !!value;
}

export function createField(initialValue, label, validator, ...validatorArgs) {
  const value = ref(initialValue);
  const touched = ref(touchedState(value.value));

  const validation = computed(() => {
    // If not touched, skip validation.
    if (!touched.value) return { valid: true, error: "" };

    // Run the validator on the current value.
    const result = validator(value.value, ...validatorArgs);
    return { valid: result.isValid, error: result.isValid ? "" : result.errorMessage };
  });

  return { label, value, touched, validation };
}

export function createToggleField(initialValue, label, errorMessage) {
  const value = ref(initialValue);
  const touched = ref(false);

  const validation = computed(() => {
    if (!touched.value) return { valid: true, error: "" };
    return { valid: value.value, error: value.value ? "" : errorMessage };
  });

  return { label, value, touched, validation };
}

export const validateEmail = (value, fieldLabel = "Email", isClient = true) => {
  if (isClient && process.server) return { isValid: true, errorMessage: "" };
  if (!value?.trim()) {
    return { isValid: false, errorMessage: getValidationMessage("requiredField").replace("{FIELDLABEL}", fieldLabel)  };
  }

   // Validate max length using the helper method
   const maxLength = Number(getMaxAllowedLength("email"));
   const maxAllowedLengthsValidationMsg = getValidationMessage("maxLength");

   // Check if the length exceeds the allowed maximum
   if (value.length > maxLength) {
     return {
       isValid: false,
       errorMessage: `${fieldLabel} ${maxAllowedLengthsValidationMsg.replace("{MAXLENGTH}", maxLength)}.`,
     };
   }

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(value)) {
    return {
      isValid: false,
      errorMessage: getValidationMessage("invalidEmail")
    };
  }



  return { isValid: true, errorMessage: "" };
};

export const validateName = (value, fieldLabel, fieldKey, isClient = true) => {
  if (isClient && process.server) return { isValid: true, errorMessage: "" };

  // Trim the value once and reuse
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return { isValid: false, errorMessage: getValidationMessage("requiredField").replace("{FIELDLABEL}", fieldLabel) };
  }

  // Combine regex for starting with a number and invalid characters
  const invalidPattern = /^[0-9]|[^a-zA-Z0-9À-ɏḀ-ỿ \-']/;
  if (invalidPattern.test(trimmedValue)) {
    return {
      isValid: false,
      errorMessage:
        /^[0-9]/.test(trimmedValue)
          ? getValidationMessage("noLeadingNumber").replaceAll("{FIELDLABEL}", fieldLabel)
          : getValidationMessage("invalidCharacter").replace("{FIELDLABEL}", fieldLabel),
    };
  }

  // Validate max length using the helper method
  const maxLength = Number(getMaxAllowedLength(fieldKey));
  const maxAllowedLengthsValidationMsg = getValidationMessage("maxLength");

  // Check if the length exceeds the allowed maximum
  if (trimmedValue.length > maxLength) {
    return {
      isValid: false,
      errorMessage: `${fieldLabel} ${maxAllowedLengthsValidationMsg.replace("{MAXLENGTH}", maxLength)}.`,
    };
  }
  // If all checks pass, return success
  return { isValid: true, errorMessage: "" };
};


/* check if string has a specific length. default length = 8 */
export const validateLength = (value:string, length:number=8):boolean => {
  return value.length >= length
}
export const validateOnlyNumbers = (value:string):boolean => {
  return new RegExp('^[+-]?\\d+$').test(value);
}

/* General rule for open field */
export const validateInputField = (value:string, fieldLabel: string, fieldKey:string, isClient: boolean = true): Object => {
  if (isClient && process.server) return { isValid: true, errorMessage: "" };

  // Trim the value once and reuse
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return { isValid: false, errorMessage: getValidationMessage("requiredField").replace("{FIELDLABEL}", fieldLabel) };
  }
  // This should be used for all textareas. For now, we only have one textarea.
  // If we'll ever add a new textarea, we should find a general rule for all textareas
  const isTextarea = fieldKey === "message";

  const textareaInvalidPattern = /^[a-zA-Z0-9À-ɏḀ-ỿ ,.!?'"@:;()\[\]{}\-_/\r\n]+$/;
  const inputInvalidPattern = /^[a-zA-Z0-9À-ɏḀ-ỿ ,;'"._/-]+$/;
  const invalidPattern = isTextarea ? textareaInvalidPattern : inputInvalidPattern;

  if (!invalidPattern.test(value)) {
    return {
      isValid: false,
      errorMessage: getValidationMessage("invalidCharacter").replace("{FIELDLABEL}", fieldLabel),
    }
  }

  // Validate max length using the helper method
  const maxLength = Number(getMaxAllowedLength(fieldKey));
  const maxAllowedLengthsValidationMsg = getValidationMessage("maxLength");

  // Check if the length exceeds the allowed maximum
  if (value.length > maxLength) {
    return {
      isValid: false,
      errorMessage: `${fieldLabel} ${maxAllowedLengthsValidationMsg.replace("{MAXLENGTH}", maxLength)}.`,
    };
  }

  // If all checks pass, return success
  return { isValid: true, errorMessage: "" };
}

/* check if string has a max specific length and is a number. default length = 10*/
export const validatePhoneNumber = (value:string, length:number=15):boolean => {
  if (!value?.trim()) {
    return false;
  }
  return value.length <= length && validateOnlyNumbers(value)
}

/* check if string has at least 1 lower and 1 upper case */
export const validateLowerCapitalLetter = (value:string):boolean => {
  return new RegExp('^(?=.*[a-z])(?=.*[A-Z])').test(value);
}

/* check if string has at least 1 number */
export const validateNumber = (value:string):boolean => {
  return new RegExp('^(?=.*[0-9])').test(value);
}

/* check if string has at least 1 number */
export const validateSpecialCharacter = (value:string, specialCharacters:string="!@#$%^&*(),.?\":;{}|<>'`~_+-"):boolean => {
  return new RegExp(`^(?=.*[${specialCharacters}])`).test(value);
}

/* check if string does not start with a number */
export const validateNotStartWithNumber = (value:string):boolean => {
  return new RegExp('^[^0-9]').test(value);
}

/* check if string does not contain emojis */
export const validateNotContainEmojis = (value:string):boolean => {
  return new RegExp('^(?!.*[^\u0000-\u00ff])').test(value);
}

export const checkIfUrlIsExternal = (url:string):boolean => {
  return typeof url === "string" && url.search(/(^\/|^#)/g) === -1
}

export const validatePostalCode = (postCode: string, countryCode: string): boolean => {
  const rule = rulesForCountries[countryCode];

  return bookOfRules[rule].formatCheck.test(postCode)
}
